import { useState } from "react";
import { ChevronRight, ChevronLeft, Shield, Clock, MapPin, Award, CheckCircle, Loader2, Home, Droplets, Zap, AlertTriangle, Calendar } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  {
    id: 1,
    type: "single",
    headline: "Let's Check Your Roof in 30 Seconds",
    question: "What best describes your situation?",
    microcopy: "This takes less than 30 seconds",
    options: [
      { value: "needs_replacement", label: "I think my roof needs to be replaced", icon: Home },
      { value: "leak_damage", label: "I have a leak or damage", icon: Droplets },
      { value: "old_unsure", label: "My roof is old but not sure what to do", icon: AlertTriangle },
      { value: "solar_prep", label: "I want to prepare for solar installation", icon: Zap },
    ],
  },
  {
    id: 2,
    type: "single",
    question: "How old is your current roof?",
    microcopy: "Most homeowners with roofs over 15 years old qualify",
    options: [
      { value: "less_5", label: "Less than 5 years" },
      { value: "5_10", label: "5–10 years" },
      { value: "10_20", label: "10–20 years" },
      { value: "20_plus", label: "20+ years" },
      { value: "not_sure", label: "Not sure" },
    ],
  },
  {
    id: 3,
    type: "multi",
    question: "Are you experiencing any of these issues?",
    microcopy: "Select all that apply",
    options: [
      { value: "leaks", label: "Leaks or water damage" },
      { value: "shingles", label: "Missing or damaged shingles" },
      { value: "energy_bills", label: "High energy bills" },
      { value: "mold", label: "Mold or moisture in attic" },
      { value: "sagging", label: "Sagging or structural concerns" },
      { value: "proactive", label: "No issues (just being proactive)" },
    ],
  },
  {
    id: 4,
    type: "single",
    question: "What type of home do you have?",
    options: [
      { value: "single_family", label: "Single-family home" },
      { value: "townhome", label: "Townhome" },
      { value: "condo", label: "Condo" },
      { value: "other", label: "Other" },
    ],
  },
  {
    id: 5,
    type: "single",
    question: "Have you filed or considered an insurance claim?",
    microcopy: "We can help with insurance paperwork",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
      { value: "need_help", label: "Not sure / need help" },
    ],
  },
  {
    id: 6,
    type: "single",
    question: "How soon are you looking to replace or inspect your roof?",
    options: [
      { value: "asap", label: "ASAP", highlight: true },
      { value: "30_days", label: "Within 30 days" },
      { value: "1_3_months", label: "1–3 months" },
      { value: "researching", label: "Just researching" },
    ],
  },
  {
    id: 7,
    type: "qualification",
    headline: "Great News!",
    subheadline: "You May Qualify for a Free Roof Inspection",
    message: "Based on your answers, you're a great candidate for our no-obligation roof assessment.",
  },
  {
    id: 8,
    type: "contact",
    headline: "Last Step – Where Should We Send Your Assessment?",
    microcopy: "Limited inspection slots available this week",
  },
];

const TRUST_BADGES = [
  { icon: Shield, text: "Licensed & Insured" },
  { icon: MapPin, text: "Serving DMV" },
  { icon: Award, text: "No Obligation" },
  { icon: Clock, text: "Fast Turnaround" },
];

export default function RoofingLeadForm() {
  const [currentStep, setCurrentStep] = useState(1);
  const [answers, setAnswers] = useState({});
  const [multiSelect, setMultiSelect] = useState([]);
  const [contactInfo, setContactInfo] = useState({
    full_name: "",
    phone: "",
    email: "",
    address: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const totalSteps = STEPS.length;
  const progress = (currentStep / totalSteps) * 100;
  const step = STEPS[currentStep - 1];

  const handleSingleSelect = (value) => {
    setAnswers({ ...answers, [step.id]: value });
    if (currentStep < totalSteps) {
      setTimeout(() => setCurrentStep(currentStep + 1), 300);
    }
  };

  const handleMultiSelect = (value) => {
    if (multiSelect.includes(value)) {
      setMultiSelect(multiSelect.filter((v) => v !== value));
    } else {
      setMultiSelect([...multiSelect, value]);
    }
  };

  const handleNext = () => {
    if (step.type === "multi") {
      setAnswers({ ...answers, [step.id]: multiSelect });
    }
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!contactInfo.full_name || !contactInfo.phone || !contactInfo.email) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      // Format the lead data
      const leadData = {
        full_name: contactInfo.full_name,
        email: contactInfo.email,
        phone: contactInfo.phone,
        address: contactInfo.address || "Not provided",
        monthly_bill: 0,
        utility_company: "N/A - Roofing Lead",
        home_ownership: answers[4] || "single_family",
        roof_shade: "none",
        lead_type: "roofing",
        // Include form answers as metadata
        situation: answers[1],
        roof_age: answers[2],
        issues: answers[3],
        home_type: answers[4],
        insurance_claim: answers[5],
        timeline: answers[6],
      };

      await axios.post(`${API}/leads`, leadData);
      setIsComplete(true);
      toast.success("Your inspection request has been submitted!");
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isComplete) {
    return (
      <section id="roof-form" className="py-16 md:py-24 bg-slate-100" data-testid="roof-form-complete">
        <div className="max-w-xl mx-auto px-6 text-center">
          <div className="bg-white rounded-3xl p-10 border border-slate-200 shadow-xl">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="font-heading text-3xl text-slate-900 mb-4">You're All Set!</h2>
            <p className="text-slate-600 text-lg mb-6">
              A roofing specialist will contact you within 24 hours to schedule your free inspection.
            </p>
            <div className="bg-emerald-50 rounded-xl p-4 text-left border border-emerald-100">
              <p className="text-emerald-700 font-medium mb-2">What happens next?</p>
              <ul className="text-slate-700 text-sm space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-600" />
                  We'll call to confirm your appointment
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-600" />
                  A licensed inspector visits your home
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-600" />
                  You receive a detailed assessment report
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="roof-form" className="py-16 md:py-24 bg-slate-100" data-testid="roof-form-section">
      <div className="max-w-xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="font-heading text-3xl md:text-4xl text-slate-900 mb-2">Quick Roof Assessment</h2>
          <p className="text-slate-600">Answer a few questions to see if you qualify for a free inspection</p>
        </div>

        {/* Trust Badges - Mobile */}
        <div className="flex flex-wrap justify-center gap-3 mb-8 md:hidden">
          {TRUST_BADGES.map((badge, i) => (
            <div key={i} className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-200">
              <badge.icon className="w-3.5 h-3.5" />
              {badge.text}
            </div>
          ))}
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-200">
          {/* Progress Bar */}
          <div className="h-2 bg-slate-200">
            <div 
              className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Step Indicator */}
          <div className="px-6 pt-4 pb-2 flex justify-between items-center border-b border-slate-200">
            <span className="text-sm text-slate-500">Step {currentStep} of {totalSteps}</span>
            {currentStep > 1 && (
              <button 
                onClick={handleBack}
                className="text-sm text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>
            )}
          </div>

          {/* Content Area */}
          <div className="p-6 md:p-8">
            {/* Single Select Step */}
            {step.type === "single" && (
              <div className="space-y-6">
                {step.headline && (
                  <h2 className="font-heading text-2xl md:text-3xl text-slate-900 text-center">
                    {step.headline}
                  </h2>
                )}
                <h3 className="text-xl md:text-2xl text-slate-800 text-center font-medium">
                  {step.question}
                </h3>
                {step.microcopy && (
                  <p className="text-sm text-slate-500 text-center">{step.microcopy}</p>
                )}
                <div className="space-y-3 mt-6">
                  {step.options.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleSingleSelect(option.value)}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all duration-200 flex items-center gap-4 ${
                        answers[step.id] === option.value
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-slate-200 hover:border-emerald-300 hover:bg-slate-50"
                      } ${option.highlight ? "ring-2 ring-emerald-200" : ""}`}
                      data-testid={`option-${option.value}`}
                    >
                      {option.icon && (
                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <option.icon className="w-5 h-5 text-emerald-600" />
                        </div>
                      )}
                      <span className="text-slate-800 font-medium">{option.label}</span>
                      {option.highlight && (
                        <span className="ml-auto text-xs bg-emerald-600 text-white px-2 py-1 rounded-full">
                          Popular
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Multi Select Step */}
            {step.type === "multi" && (
              <div className="space-y-6">
                <h3 className="text-xl md:text-2xl text-slate-800 text-center font-medium">
                  {step.question}
                </h3>
                {step.microcopy && (
                  <p className="text-sm text-slate-500 text-center">{step.microcopy}</p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
                  {step.options.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleMultiSelect(option.value)}
                      className={`p-4 rounded-xl border-2 text-left transition-all duration-200 flex items-center gap-3 ${
                        multiSelect.includes(option.value)
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-slate-200 hover:border-emerald-300"
                      }`}
                      data-testid={`multi-${option.value}`}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        multiSelect.includes(option.value)
                          ? "border-emerald-500 bg-emerald-500"
                          : "border-slate-300"
                      }`}>
                        {multiSelect.includes(option.value) && (
                          <CheckCircle className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <span className="text-slate-700 text-sm">{option.label}</span>
                    </button>
                  ))}
                </div>
                <Button
                  onClick={handleNext}
                  disabled={multiSelect.length === 0}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 rounded-xl font-bold text-lg mt-4"
                >
                  Continue
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}

            {/* Qualification Step */}
            {step.type === "qualification" && (
              <div className="text-center space-y-6 py-4">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <h2 className="font-heading text-3xl text-emerald-600">{step.headline}</h2>
                <h3 className="text-2xl text-slate-800 font-medium">{step.subheadline}</h3>
                <p className="text-slate-600">{step.message}</p>
                <div className="bg-emerald-50 rounded-xl p-4 text-left mt-6 border border-emerald-100">
                  <p className="text-emerald-700 text-sm font-medium mb-2">Your Qualification Summary:</p>
                  <ul className="text-slate-700 text-sm space-y-1">
                    <li>✓ Eligible for free inspection</li>
                    <li>✓ No obligation assessment</li>
                    <li>✓ Licensed & insured team</li>
                  </ul>
                </div>
                <Button
                  onClick={handleNext}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 rounded-xl font-bold text-lg"
                >
                  Continue to Final Step
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}

            {/* Contact Info Step */}
            {step.type === "contact" && (
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="font-heading text-2xl md:text-3xl text-slate-900 mb-2">
                    {step.headline}
                  </h2>
                  {step.microcopy && (
                    <p className="text-sm text-amber-600 font-medium flex items-center justify-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {step.microcopy}
                    </p>
                  )}
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={contactInfo.full_name}
                      onChange={(e) => setContactInfo({ ...contactInfo, full_name: e.target.value })}
                      placeholder="John Smith"
                      className="w-full h-12 px-4 rounded-xl bg-slate-50 text-slate-900 border-2 border-slate-200 focus:border-emerald-500 focus:ring-0 transition-colors placeholder-slate-400"
                      data-testid="contact-name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={contactInfo.phone}
                      onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })}
                      placeholder="(301) 555-1234"
                      className="w-full h-12 px-4 rounded-xl bg-slate-50 text-slate-900 border-2 border-slate-200 focus:border-emerald-500 focus:ring-0 transition-colors placeholder-slate-400"
                      data-testid="contact-phone"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={contactInfo.email}
                      onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
                      placeholder="john@example.com"
                      className="w-full h-12 px-4 rounded-xl bg-slate-50 text-slate-900 border-2 border-slate-200 focus:border-emerald-500 focus:ring-0 transition-colors placeholder-slate-400"
                      data-testid="contact-email"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Property Address
                    </label>
                    <input
                      type="text"
                      value={contactInfo.address}
                      onChange={(e) => setContactInfo({ ...contactInfo, address: e.target.value })}
                      placeholder="123 Main St, Bethesda, MD"
                      className="w-full h-12 px-4 rounded-xl bg-slate-50 text-slate-900 border-2 border-slate-200 focus:border-emerald-500 focus:ring-0 transition-colors placeholder-slate-400"
                      data-testid="contact-address"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 rounded-xl font-bold text-lg shadow-lg shadow-emerald-200"
                  data-testid="submit-roof-form"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Get My Free Roof Inspection"
                  )}
                </Button>

                <p className="text-xs text-slate-500 text-center">
                  By submitting, you agree to be contacted about your roof inspection.
                  Your information is secure and will never be shared.
                </p>
              </div>
            )}
          </div>

          {/* Trust Badges - Desktop */}
          <div className="hidden md:flex justify-center gap-6 px-6 py-4 bg-slate-50 border-t border-slate-200">
            {TRUST_BADGES.map((badge, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                <badge.icon className="w-4 h-4 text-emerald-600" />
                {badge.text}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
